<template>
<v-container class="min-h-screen px-4" >
  
  <div class="logo position-relative ">
    <a href="/" class="d-block text-decoration-none position-relative">
      <img
      src="@/assets/images/logos/LOGO-1.png"
      alt="logo-icon"
          class="logo-icon"
          max-width="60"
          width="45"
          height="auto"
          />
          <span class="logo-text fw-bold text-dark">MERO PRINTS</span>
        </a>
        <button
        class="sidebar-burger-menu bg-transparent p-0 border-0 opacity-0 z-n1 position-absolute top-50 end-0 translate-middle-y d-md-none"
        id="sidebar-burger-menu"
        @click="stateStoreInstance.onChange"
        >
        <i data-feather="x"></i>
      </button>
    </div>
    
    <!-- Circular Steps -->
    <div class="steps-container d-flex justify-center mb-6">
      <div
        v-for="step in totalSteps"
        :key="step"
        :class="['step', {
          active: currentStep === step,
          completed: isStepCompleted(step),
          disabled: !isStepAccessible(step)
        }]"
        @click="goToStep(step)"
      >
        <span>{{ step }}</span>
      </div>
    </div>
    
    <!-- Card for Forms -->
     <component 
    :is="currentStep === 3 ? 'div' : 'v-card'" 
    :class="currentStep !== 3 
    ? ['pl-6 pr-6 pb-6 rounded-lg shadow-lg', 'w-full md:max-w-800'] 
    : ['mt-8 ']" 
    :style="currentStep === 3 
      ? { width: '100%', maxWidth: '800px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', height:'auto' }
      : { width: '100%', maxWidth: '800px' }"
  >
      <!-- Step 1: School Details -->
      <template v-if="currentStep === 1">
        <div class="reg-title text-center mb-4 mt-4">School Details</div>
        <SchoolDetailsForm ref="schoolForm" :form="form" />
      </template>

      <!-- Step 2: User Details -->
      <template v-else-if="currentStep === 2">
        <div class="reg-title text-center mb-4 mt-4">User Details</div>
        <UserDetailsForm ref="userForm" :form="form" />
      </template>

      <!-- Step Navigation Buttons -->
    <v-row :justify="currentStep === 1 ? 'end pr-2' : 'space-between'" class="mt-4">
      <v-btn 
        v-if="currentStep > 1 && currentStep !== 3" 
        @click="prevStep" 
        color="primary" 
        outlined
      >
        Previous
      </v-btn>
      <v-btn 
        v-if="currentStep < totalSteps" 
        @click="nextStep" 
        color="primary"
        :disabled="!isCurrentStepValid"
      >
        Next
      </v-btn>
      <SubmitButton 
        v-if="currentStep === totalSteps" 
        :handleSignUp="handleSignUp" 
        :form="form" 
      />
    </v-row>

      <!-- Snackbar for feedback -->
      <!-- <v-snackbar v-model="showFeedback" :color="feedbackColor" timeout="1000" right top absolute>
        <v-icon class="me-2">{{ feedbackIcon }}</v-icon>
        {{ feedback.message }}
      </v-snackbar> -->
    </component>
  </v-container>
</template>

<script setup>
import AvatarComponent from "@/components/Modules/Authentication/Register/AvatarComponent.vue";
import SchoolDetailsForm from "@/components/Modules/Authentication/Register/SchoolDetailsForm.vue";
import UserDetailsForm from "@/components/Modules/Authentication/Register/UserDetailsForm.vue";
import SubmitButton from "@/components/Modules/Authentication/Register/SubmitButton.vue";
import { ref, reactive, computed,watch } from "vue";
import { register } from "@/services/apiServices.js";
import Swal from "sweetalert2";
import { useRouter } from "vue-router";

const router = useRouter();
const form = reactive({
  schoolName: "",
  schoolContact: "",
  schoolAddress: "",
  principalName: "",
  first_name: "",
  last_name: "",
  contact: "",
  email: "",
  password: "",
});

const feedback = reactive({ type: "", message: "" });
const showFeedback = ref(false);
const currentStep = ref(1);
const totalSteps = 3;
const schoolForm = ref(null);
const userForm = ref(null);

// Track validation state for each step
const stepValidations = reactive({
  1: false,
  2: false,
  3: true // Step 3 doesn't need validation
});

watch(() => schoolForm.value?.valid, (valid) => {
  if (valid !== undefined) stepValidations[1] = valid;
});

watch(() => userForm.value?.valid, (valid) => {
  if (valid !== undefined) stepValidations[2] = valid;
});

// Check if current step is valid
const isCurrentStepValid = computed(() => {
  // console.log("Current Step Valid:", isCurrentStepValid.value);
  // console.log("Current Step:", currentStep.value);
  return stepValidations[currentStep.value];
});

// Check if step is completed (all fields valid)
const isStepCompleted = (step) => {
  return currentStep.value > step;
};

// Check if step is accessible (clickable)
const isStepAccessible = (step) => {
  // Always allow going back
  if (step < currentStep.value) return true;
  
  // For future steps, all previous must be valid
  for (let i = 1; i < step; i++) {
    if (!stepValidations[i]) return false;
  }
  return true;
};

const goToStep = (step) => {
  if (isStepAccessible(step)) {
    currentStep.value = step;
  }
};

const nextStep = async () => {
  let isValid = false;
  
  if (currentStep.value === 1) {
    isValid = (await schoolForm.value?.validate())?.valid ?? false;
    stepValidations[1] = isValid;
  } 
  else if (currentStep.value === 2) {
    isValid = (await userForm.value?.validate())?.valid ?? false;
    stepValidations[2] = isValid;
  }
  
  if (isValid) {
    currentStep.value++;
    showFeedbackMessage("success", `Step ${currentStep.value - 1} completed!`);
  } else {
    showFeedbackMessage("error", "Please fix form errors");
  }
};

const prevStep = () => {
  currentStep.value--;
};


const showFeedbackMessage = (type, message) => {
  feedback.type = type;
  feedback.message = message;
  showFeedback.value = true;
};

const feedbackColor = computed(() =>
  feedback.type === "success" ? "green" : "red"
);

const feedbackIcon = computed(() =>
  feedback.type === "success" ? "mdi-check-circle" : "mdi-alert-circle"
);


const validateStep = (step) => {
  switch (step) {
    case 1:
      return (
        form.schoolName &&
        form.schoolContact &&
        form.schoolAddress &&
        form.principalName
      );
    case 2:
      return (
        form.first_name &&
        form.last_name &&
        form.contact &&
        form.email &&
        form.password
      );
    case 3:
      return true; 
    default:
      return false;
  }
};

const handleSignUp = async () => {
  if (!validateStep(currentStep.value)) {
    feedback.value = { type: "error", message: "Form is not valid!" };
    return;
  }
  try {
    feedback.value = { type: "info", message: "Registering, please wait..." };
    const res = await register(form);
    router.push("/authentication/otp?contact=" + form.contact);
    localStorage.setItem("Authorization", `Bearer ${res.access}`)
    
  } catch {
    feedback.value = {
      type: "error",
      message: "An error occurred. Please try again later.",
    };
  }
};
</script>

<style scoped>
.steps-container {
  display: flex;
  justify-content: center;
  gap: 30px; /* Increased gap for more space */
  margin-bottom: 40px;
}

.step {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  position: relative;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.step.active {
  background-color: #ff9800;
  transform: scale(1.1);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
}

.step.completed {
  background-color: #4caf50;
  transform: scale(1.1);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
}

.step span {
  font-size: 18px;
  z-index: 1;
  font-weight: bold;
}

.step:hover {
  background-color: #ffb74d;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.v-card {
  max-width: 100%;
  margin: auto;
  border-radius: 15px;
}

.v-btn {
  transition: background-color 0.3s, transform 0.3s;
}

.v-btn:hover {
  background-color: #0056b3;
  transform: scale(1.05);
}

.v-snackbar {
  font-size: 14px;
}

.v-row {
  margin-top: 20px;
}

/* Responsive Adjustments */
@media (max-width: 767px) {
  .step {
    width: 30px;
    height: 30px;
  }

  .v-card {
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
  }

  .sidebar-burger-menu {
    display: block;
  }
}

@media (min-width: 768px) {
  .steps-container {
    gap: 15px; /* Adjust gap for medium screens */
  }
}
</style>
